import React from "react";
import { InputField, ReactSelect, required, ToggleField } from "modules/4_final_form/web";
import { Fragment } from "react";
import { Col, FormGroup, Label } from "reactstrap";
import { Field, useFormState } from "react-final-form";

export const getColsAttributes = (values, position) => {
  const latest_revision = _.last(values?.revisions_attributes);
  let pages_attributes = latest_revision?.pages_attributes;
  if (position) {
    pages_attributes = _.filter(pages_attributes, (p) => p.position <= position);
  }
  const data = _.flatMap(pages_attributes, "data");
  const rows_attributes = _.flatMap(data, "rows_attributes");
  const cols_attributes = _.flatMap(rows_attributes, "cols_attributes");
  return _.compact(cols_attributes);
};

// ponytail: one-shot list fetches for form fields — ProjectVariation still loads per selected project
export const fetchRequiredFormFieldData = (cols, actions) => {
  if (_.some(cols, { column_type: "employee" })) {
    actions.getEmployees?.();
  }
  if (_.some(cols, (c) => c.column_type === "project" || c.column_type === "project_variation")) {
    actions.getProjects?.(true);
  }
  if (_.some(cols, { column_type: "user" })) {
    actions.getUserManagements?.(true);
  }
  if (_.some(cols, { column_type: "company" })) {
    actions.getAddressBookCompanies?.();
  }
};

export const conditionalCheck = (
  column_type,
  conditional_value_required,
  conditional_select_value_required,
  conditional_boolean_value,
  current_input_value
) => {
  let check = false;
  if (column_type == "toggle") {
    check = current_input_value != conditional_boolean_value;
  } else if (_.isArray(conditional_select_value_required)) {
    const conditional_values = _.map(conditional_select_value_required, (v) => v.label.toLowerCase());
    if (_.some(conditional_values, "Present")) {
      check = !_.isNotEmpty(current_input_value);
    } else {
      check = !conditional_values.includes(current_input_value?.toLowerCase());
    }
  } else {
    check = conditional_value_required != conditionalValue;
  }

  return check;
};

export const ConditionalFieldsInputs = (props) => {
  const { revisionIndex, position, uid, conditional_field_name, conditional_selected_name, conditional_boolean_name, conditional_value_name } = props;

  const formState = useFormState();

  let pagesToCheck = formState.values.revisions_attributes[revisionIndex]?.pages_attributes;
  if (position) {
    pagesToCheck = _.filter(pagesToCheck, (p) => p.position < position);
  }

  const data = _.flatMap(pagesToCheck, "data");
  const rows_attributes = _.flatMap(data, "rows_attributes");
  const cols_attributes = _.flatMap(rows_attributes, "cols_attributes");
  const filteredColsAttributes = _.filter(cols_attributes, (c) => c?.uid != uid && !c?._destroy);
  const AllowedConditonalFields = _.filter(
    _.compact(filteredColsAttributes),
    (c) => c.column_type != "text" && c.column_type != "attachments" && c.column_type != "project_variation" && c.column_type != "alert"
  );

  const ConditionalField = _.get(formState.values, conditional_field_name);
  const ConditionalFieldObject = _.find(filteredColsAttributes, { name: ConditionalField });
  let conditions = [{ label: "Present", value: "present" }];
  if (ConditionalFieldObject?.column_type == "toggle") {
    conditions = [
      { label: "True", value: "true" },
      { label: "False", value: "false" },
    ];
  }
  if (ConditionalFieldObject?.column_type == "yesnona") {
    conditions = [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
      { label: "N/A", value: "N/A" },
    ];
  }
  const isSelect = ConditionalFieldObject && (ConditionalFieldObject.column_type == "select" || ConditionalFieldObject.column_type == "multiselect");
  const isToggle = ConditionalFieldObject && ConditionalFieldObject.column_type == "toggle";
  const isYesNoNa = ConditionalFieldObject && ConditionalFieldObject.column_type == "yesnona";
  const isProject = ConditionalFieldObject && ConditionalFieldObject.column_type == "project";

  return (
    <Fragment>
      <FormGroup row>
        <Label md={3}>Condition Field</Label>
        <Col md={8}>
          <Field
            name={conditional_field_name}
            component={ReactSelect}
            options={_.map(AllowedConditonalFields, (ca) => {
              return { label: ca.label, value: ca.name };
            })}
            validate={required}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label md={3}>Condition Value</Label>
        <Col md={8}>
          {isSelect && (
            <Field
              component={ReactSelect}
              name={conditional_selected_name}
              multi
              options={_.map(ConditionalFieldObject?.options, (o) => ({ label: o.value, value: o.value }))}
              parse={(value) => (_.isArray(value) ? value : [])}
            />
          )}
          {isYesNoNa && (
            <Field
              component={ReactSelect}
              name={conditional_selected_name}
              multi
              options={[
                { label: "Yes", value: "Yes" },
                { label: "No", value: "No" },
                { label: "N/A", value: "N/A" },
              ]}
              parse={(value) => (_.isArray(value) ? value : [])}
            />
          )}
          {isToggle && <Field component={ToggleField} name={conditional_boolean_name} />}
          {!isSelect && !isToggle && !isYesNoNa && !isProject && <Field component={InputField} name={conditional_value_name} parse={(v) => v} />}
        </Col>
      </FormGroup>
    </Fragment>
  );
};

// Hold point: when THIS field's value matches hold_point_value, form can only save as draft
export const valueMatchesHold = (column_type, hold_point_value, current_value) => {
  if (column_type == "toggle" || column_type == "checkbox") {
    // toggles persist as "1"/"0" — !!"0" is true, so don't use bare truthiness
    const isOn = current_value === true || current_value == "1" || current_value == "true";
    if (hold_point_value === true || hold_point_value == "true") return isOn;
    if (hold_point_value === false || hold_point_value == "false") return !isOn;
    return false;
  }
  if (_.isArray(hold_point_value)) {
    const hold_values = _.map(hold_point_value, (v) => (v.label || v.value || v)?.toString?.()?.toLowerCase?.());
    if (_.isArray(current_value)) {
      const current_values = _.map(current_value, (v) => (v?.value || v)?.toString?.()?.toLowerCase?.());
      return _.some(hold_values, (val) => current_values.includes(val));
    }
    // select values are usually strings; occasionally { label, value }
    const current =
      _.isObject(current_value) && !_.isArray(current_value)
        ? (current_value.value || current_value.label)?.toString?.()?.toLowerCase?.()
        : current_value?.toString?.()?.toLowerCase?.();
    return _.includes(hold_values, current);
  }
  if (_.isNil(hold_point_value) || hold_point_value === "") return false;
  return hold_point_value == current_value;
};

// ponytail: stash under comments so we don't need a new column; CommentsPage skips unknown keys
export const holdBypassPath = (name) => `comments.__hold_bypasses.${name}`;

export const isHoldBypassed = (col, values) => {
  if (!col?.hold_point_bypassable) return false;
  const bypass = _.get(values, holdBypassPath(col.name));
  return !!bypass?.bypassed && _.trim(bypass?.comment || "") !== "";
};

export const getMatchingHoldPoints = (cols, values) => {
  return _.filter(cols, (col) => {
    if (!col?.hold_point || col?._destroy) return false;
    const current = _.get(values?.data, col.name);
    return valueMatchesHold(col.column_type, col.hold_point_value, current);
  });
};

// Blocking holds only — matching holds that aren't properly bypassed
export const getActiveHoldPoints = (cols, values) => {
  return _.filter(getMatchingHoldPoints(cols, values), (col) => !isHoldBypassed(col, values));
};

export const HoldPointValueInputs = (props) => {
  const { fieldName, column } = props;
  const isSelect = column?.column_type == "select" || column?.column_type == "multiselect";
  const isToggle = column?.column_type == "toggle";
  const isYesNoNa = column?.column_type == "yesnona";

  return (
    <Fragment>
      <FormGroup row>
        <Label md={3}>Hold when value is</Label>
        <Col md={8}>
          {isSelect && (
            <Field
              component={ReactSelect}
              name={`${fieldName}.hold_point_value`}
              multi
              options={_.map(column?.options, (o) => ({ label: o.value, value: o.value }))}
              parse={(value) => (_.isArray(value) ? value : [])}
              validate={required}
            />
          )}
          {isYesNoNa && (
            <Field
              component={ReactSelect}
              name={`${fieldName}.hold_point_value`}
              multi
              options={[
                { label: "Yes", value: "Yes" },
                { label: "No", value: "No" },
                { label: "N/A", value: "N/A" },
              ]}
              parse={(value) => (_.isArray(value) ? value : [])}
              validate={required}
            />
          )}
          {isToggle && <Field component={ToggleField} name={`${fieldName}.hold_point_value`} />}
          {!isSelect && !isToggle && !isYesNoNa && (
            <Field component={InputField} name={`${fieldName}.hold_point_value`} parse={(v) => v} validate={required} />
          )}
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label md={3}>Hold message</Label>
        <Col md={8}>
          <Field
            component={InputField}
            name={`${fieldName}.hold_point_message`}
            placeholder="Hold point active — save as draft only"
            parse={(v) => v}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label md={3}>Allow Bypass</Label>
        <Col md={3}>
          <Field name={`${fieldName}.hold_point_bypassable`} component={ToggleField} />
        </Col>
      </FormGroup>
    </Fragment>
  );
};
